import * as React from 'react';
import { auth, db } from "../config/firebase";
import { getDoc, setDoc, doc } from "firebase/firestore";
import { useAuthState } from 'react-firebase-hooks/auth';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import AlertTitle from '@mui/material/AlertTitle';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import "../style/BBCode.css";
import "../style/Editor.css";
import "../style/Variable.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { faImage } from '@fortawesome/free-solid-svg-icons';
import IconButton from '@mui/material/IconButton';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { v4 as uuidv4 } from 'uuid';
import Button from 'react-bootstrap/Button';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/en-gb';
import ListEditor from "./ListEditor.js";

const locales = ['en', 'en-gb', 'de'];

function BBCodeEditor(props) {

    const [user] = useAuthState(auth);

    const [editingUser, setEditingUser] = React.useState(null);

    const templateRef = React.useRef();
    const simpleBarRef = React.useRef();

    const highlightVarID = (id) => {
        const variableIdToFind = `{${id}}`;
        const inputElement = templateRef.current.querySelector("textarea");
        const inputText = inputElement.value;
        const startIndex = inputText.indexOf(variableIdToFind);
      
        if (startIndex !== -1) {
          const endIndex = startIndex + variableIdToFind.length;
          inputElement.focus();
          inputElement.setSelectionRange(startIndex, endIndex);
      
          const lineHeight = parseFloat(window.getComputedStyle(inputElement).lineHeight);
          const linesBefore = inputText.slice(0, startIndex).split('\n').length;
          const scrollPos = lineHeight * (linesBefore - 1);
          simpleBarRef.current.getScrollElement().scrollTop = scrollPos;
        }
    };

    const [entryTitle, setEntryTitle] = React.useState(null);
    const [entryIndex, setEntryIndex] = React.useState(null);
    const [entryTemplate, setEntryTemplate] = React.useState(null);
    const [entryVars, setEntryVars] = React.useState(null);

    const [locale, setLocale] = React.useState('en');

    const addVariable = () => {
        const newID = uuidv4();

        const newVariable = {
            id: newID,
            name: "",
            description: "",
            type: "text",
            default: "",
            inTemplate: false
        };

        const newEntryVars = entryVars.concat(newVariable);

        setEntryVars(newEntryVars);
    };

    const removeVariableFromTemplate = (id) => {
        const variableIdToRemove = `{${id}}`;
        const inputElement = templateRef.current.querySelector("textarea");
        const inputText = inputElement.value;
        const updatedText = inputText.split(variableIdToRemove).join('');
        setEntryTemplate(updatedText);
    };    

    const deleteVariable = (id) => {
        const newEntryVars = entryVars.filter(variable => variable.id !== id);
        setEntryVars(newEntryVars);
        removeVariableFromTemplate(id);
    };
    
    const moveVariable = (id, direction) => {
        setEntryVars((prevEntryVars) => {
          const index = prevEntryVars.findIndex((variable) => variable.id === id);
          if (index < 0) return prevEntryVars; // variable not found
      
          const newIndex = direction === 'up' ? index - 1 : index + 1;
          if (newIndex < 0 || newIndex >= prevEntryVars.length) return prevEntryVars; // out of bounds
      
          const updatedEntryVars = [...prevEntryVars];
          [updatedEntryVars[index], updatedEntryVars[newIndex]] = [updatedEntryVars[newIndex], updatedEntryVars[index]]; // swap elements
      
          return updatedEntryVars;
        });
      };      

    const changeVarName = (id, name) => {
        setEntryVars(prevEntryVars => {
            const index = prevEntryVars.findIndex(variable => variable.id === id);
            const updatedVariable = {
                ...prevEntryVars[index],
                name: name
            };
            return [
                ...prevEntryVars.slice(0, index),
                updatedVariable,
                ...prevEntryVars.slice(index + 1)
            ];
        });
    };

    const changeVarDesc = (id, desc) => {
        setEntryVars(prevEntryVars => {
            const index = prevEntryVars.findIndex(variable => variable.id === id);
            const updatedVariable = {
                ...prevEntryVars[index],
                description: desc
            };
            return [
                ...prevEntryVars.slice(0, index),
                updatedVariable,
                ...prevEntryVars.slice(index + 1)
            ];
        });
    };
    
    const changeVarType = (id, type) => {
        setEntryVars(prevEntryVars => {
            const index = prevEntryVars.findIndex(variable => variable.id === id);
            const updatedVariable = {
                ...prevEntryVars[index],
                type: type
            };
            return [
                ...prevEntryVars.slice(0, index),
                updatedVariable,
                ...prevEntryVars.slice(index + 1)
            ];
        });
    };
    
    const changeVarDefaultValue = (id, defaultValue, textFieldType = null) => {
        setEntryVars(prevEntryVars => {
          const index = prevEntryVars.findIndex(variable => variable.id === id);
          const updatedVariable = {
            ...prevEntryVars[index],
            default: textFieldType
              ? { ...prevEntryVars[index].default, [textFieldType]: defaultValue }
              : defaultValue,
          };
          return [
            ...prevEntryVars.slice(0, index),
            updatedVariable,
            ...prevEntryVars.slice(index + 1)
          ];
        });
    };

    const saveTemplate = async () => {
        if (!entryTitle || entryTitle.trim() === "") {
            alert("Please enter a title for the template before saving.");
            return;
        }
        
        const emptyVarName = entryVars.some(variable => !variable.name || variable.name.trim() === "");
        if (emptyVarName) {
            alert("Please enter a name for all variables before saving.");
            return;
        }


        // Make a copy of the current variables list
        const updatedVars = entryVars.map((variable) => {
            if (
            (variable.type === "text" ||
                variable.type === "text_long" ||
                variable.type === "image_url") &&
            typeof variable.default === "object"
            ) {
            // Return a new variable object with an empty string as the default value
            return { ...variable, default: "" };
            } else if (variable.type === "hyperlink" && typeof variable.default === "object") {
            // Return a new variable object with only 'url' and 'displayText' in the default map
            return { ...variable, default: { url: variable.default.url || '', displayText: variable.default.displayText || '' } };
            } else if ((variable.type === "date" || variable.type === "time" || variable.type === "datetime") && typeof variable.default === "object") {

            }
            // Return the original variable object if the conditions are not met
            return variable;
        });

        try {
            await setDoc(doc(db, "bbcode", props.entryID), {
                index: entryIndex,
                title: entryTitle,
                user: auth.currentUser.email,
                locale: locale,
                template: entryTemplate,
                vars: updatedVars,
            });
            window.location.reload();
        } catch (err) {
            alert("Error saving the template. Please try again.");
            console.error(err);
        }
    };

    React.useEffect(() => {
        const importEntry = async (entryID) => {
            try {
                const entryDoc = await getDoc(doc(db, 'bbcode', entryID));
                if (entryDoc.exists()) {
                    const tempEntryData = entryDoc.data();
                    setEntryIndex(tempEntryData.index);
                    setEntryTitle(tempEntryData.title);
                    setLocale(tempEntryData.locale);
                    setEntryTemplate(tempEntryData.template);
                    setEntryVars(tempEntryData.vars);
                }
                else {
                    alert("Error: Template not found in database. Please try again.");
                }
            } catch (err) {
                alert("Error: Template not found in database. Please try again.");
                console.error(err);
            }
        };

        importEntry(props.entryID);

        setEditingUser(user);
        console.log(user);
    }, []);

    React.useEffect(() => {
        const ids = entryVars?.map(variable => variable.id);
        const hasInTemplate = entryVars?.some((entry) => entry.inTemplate === true);
        const updateEntryVar = (id, show) => {
            setEntryVars(prevEntryVars => {
              const index = prevEntryVars.findIndex((entry) => entry.id === id);
              const updatedEntry = {
                ...prevEntryVars[index],
                inTemplate: show
              };
              return [
                ...prevEntryVars.slice(0, index),
                updatedEntry,
                ...prevEntryVars.slice(index + 1)
              ];
            });
          };

        if (entryTemplate?.indexOf("{") > -1) {
            const pattern = /\{([^}]+)\}/g;
            const matches = [];
            let match;

            while ((match = pattern.exec(entryTemplate)) !== null) {
                matches.push(match[1]);
            }

            for (const id of ids) {
                if (matches.includes(id)) {
                    updateEntryVar(id, true);
                } else {
                    updateEntryVar(id, false);
                }
            }
        } else if (hasInTemplate) {
            for (const id of ids) {
                updateEntryVar(id, false);
            }
        }
    }, [entryTemplate, !entryTemplate]);

    return (
        <React.Fragment>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
                {user?.email === editingUser?.email ? 
                    <div>
                        <div className="Editor_Div">
                            <div style={{width: 600, textAlign: "left"}}>
                                <h2>Variables</h2>
                                <div style={{display: "inline-flex", width: 600, marginTop: 5}}>
                                    <div>
                                        <TextField required variant="standard" size="small" label="Template Name" value={entryTitle || ''} onChange={(e) => setEntryTitle(e.target.value)} style={{width: 350, marginTop: 20}}></TextField>
                                    </div>
                                    <div style={{width: 250, display: "flex", justifyContent: "end", alignItems: "end"}}>
                                        <Button variant="dark" onClick={addVariable}><FontAwesomeIcon icon={faPlus} /> Add Variable</Button>
                                    </div>
                                </div>
                                <Divider sx={{bgcolor: "#5d4ef3", marginTop: 2}} />
                                <SimpleBar className="Variables">
                                    <Stack spacing={2}>
                                        {entryVars ? entryVars.map((variable) => (
                                            <Paper key={variable.id} elevation={5} style={{margin: 15, padding: 15}}>
                                                <div style={{display: "flex", justifyContent: "space-between"}}>
                                                    <TextField required size="small" style={{width: 350, marginBottom: 15}} label="Variable Name" value={variable.name} onChange={(e) => changeVarName(variable.id, e.target.value)}></TextField>
                                                    <div>
                                                        <IconButton onClick={() => moveVariable(variable.id, 'up')} disabled={entryVars.indexOf(variable) === 0} style={{marginBottom: 15, color: "grey", fontSize: 20}}><ArrowUpwardIcon className={entryVars.indexOf(variable) === 0 ? "Disabled_Button" : ''} /></IconButton>
                                                        <IconButton onClick={() => moveVariable(variable.id, 'down')} disabled={entryVars.indexOf(variable) === entryVars.length - 1} style={{marginBottom: 15, color: "grey", fontSize: 20}}><ArrowDownwardIcon className={entryVars.indexOf(variable) === entryVars.length - 1 ? "Disabled_Button" : ''} /></IconButton>
                                                    </div>
                                                    <IconButton variant="text" onClick={() => deleteVariable(variable.id)} style={{marginBottom: 15, color: "grey", fontSize: 20}}><CloseIcon /></IconButton>
                                                </div>
                                                <div style={{display: "flex"}}>
                                                <TextField size="small" style={{width: 350, marginBottom: 15}} label="Variable Description" value={variable.description} onChange={(e) => changeVarDesc(variable.id, e.target.value)}></TextField>
                                                <div style={{padding: 10}}></div>
                                                <FormControl variant="outlined">
                                                    <InputLabel htmlFor={`type-${variable.id}`}>Type</InputLabel>
                                                    <Select label="Type" size="small" id={`type-${variable.id}`} value={variable.type} autoWidth style={{marginBottom: 15}} onChange={(e) => changeVarType(variable.id, e.target.value)}>
                                                        <MenuItem value="text">Text</MenuItem>
                                                        <MenuItem value="text_long">Multi-line Text</MenuItem>
                                                        <MenuItem value="ordered_list">Numbered List</MenuItem>
                                                        <MenuItem value="unordered_list">Bulleted List</MenuItem>
                                                        <MenuItem value="date">Date</MenuItem>
                                                        <MenuItem value="time">Time</MenuItem>
                                                        <MenuItem value="datetime">Date & Time</MenuItem>
                                                        <MenuItem value="hyperlink">Hyperlink</MenuItem>
                                                        <MenuItem value="image_url">Image URL</MenuItem>
                                                    </Select>
                                                </FormControl>
                                                </div>
                                                <div>
                                                    {variable.type === "text" ? <TextField size="small" className="Text_Editor" style={{marginBottom: 15}} label="Default" id={`default-${variable.id}`} value={typeof variable.default === 'object' ? '' : variable.default} onChange={(e) => changeVarDefaultValue(variable.id, e.target.value)}></TextField> :null}
                                                    {variable.type === "text_long" ? <TextField size="small" className="Text_Long_Editor" style={{marginBottom: 15}} multiline label="Default" id={`default-${variable.id}`} value={typeof variable.default === 'object' ? '' : variable.default} onChange={(e) => changeVarDefaultValue(variable.id, e.target.value)}></TextField> :null}
                                                    {variable.type === "date" ? <div style={{display: "flex", marginBottom: 15}}><DatePicker className="Text_Editor" label="Default" id={`default-${variable.id}`} value={dayjs(new Date(variable.default))} onChange={(value) => changeVarDefaultValue(variable.id, value.toString())} slotProps={{ textField: { size: 'small' } }}></DatePicker><ToggleButtonGroup size="small" sx={{maxWidth: 190}} value={locale} exclusive fullWidth onChange={(event, newLocale) => setLocale(newLocale)}> {locales.map((localeItem) => (<ToggleButton key={localeItem} value={localeItem}>{localeItem}</ToggleButton>))}</ToggleButtonGroup></div> :null}
                                                    {variable.type === "time" ? <div style={{display: "flex", marginBottom: 15}}><TimePicker className="Text_Editor" label="Default" id={`default-${variable.id}`} value={dayjs(new Date(variable.default))} onChange={(value) => changeVarDefaultValue(variable.id, value.toString())} slotProps={{ textField: { size: 'small' } }}></TimePicker><ToggleButtonGroup size="small" sx={{maxWidth: 190}} value={locale} exclusive fullWidth onChange={(event, newLocale) => setLocale(newLocale)}> {locales.map((localeItem) => (<ToggleButton key={localeItem} value={localeItem}>{localeItem}</ToggleButton>))}</ToggleButtonGroup></div> :null}
                                                    {variable.type === "datetime" ? <div style={{display: "flex", marginBottom: 15}}><DateTimePicker className="Text_Editor" label="Default" id={`default-${variable.id}`} value={dayjs(new Date(variable.default))} onChange={(value) => changeVarDefaultValue(variable.id, value.toString())} slotProps={{ textField: { size: 'small' } }}></DateTimePicker><ToggleButtonGroup size="small" sx={{maxWidth: 190}} value={locale} exclusive fullWidth onChange={(event, newLocale) => setLocale(newLocale)}> {locales.map((localeItem) => (<ToggleButton key={localeItem} value={localeItem}>{localeItem}</ToggleButton>))}</ToggleButtonGroup></div> :null}
                                                    {variable.type === "hyperlink" ? (<div style={{ display: "flex", alignItems: "center" }}><FontAwesomeIcon icon={faLink} style={{ marginBottom: 15, paddingRight: 7, color: "gray" }} /><TextField size="small" className="Hyperlink_Editor" style={{ marginBottom: 15 }} label="Default URL" id={`default-${variable.id}-url`} value={variable.default.url || ""} onChange={(e) => changeVarDefaultValue(variable.id, e.target.value, "url")}></TextField><TextField size="small" className="Hyperlink_Editor" style={{ marginBottom: 15 }} label="Default Display Text" id={`default-${variable.id}-displayText`} value={variable.default.displayText || ""} onChange={(e) => changeVarDefaultValue(variable.id, e.target.value, "displayText")}></TextField></div>) :null}
                                                    {variable.type === "image_url" ? <div style={{display: "flex", alignItems: "center"}}><FontAwesomeIcon icon={faImage} style={{marginBottom: 15, paddingRight: 7, color: "gray"}} /><TextField size="small" className="Image_URL_Editor" style={{marginBottom: 15}} label="Default URL" id={`default-${variable.id}`} value={typeof variable.default === 'object' ? '' : variable.default} onChange={(e) => changeVarDefaultValue(variable.id, e.target.value)}></TextField></div> :null}
                                                    {variable.type === "ordered_list" ? <ListEditor variableId={variable.id} changeDefault={changeVarDefaultValue} variableDefault={variable.default}></ListEditor> :null}
                                                    {variable.type === "unordered_list" ? <ListEditor variableId={variable.id} changeDefault={changeVarDefaultValue} variableDefault={variable.default}></ListEditor> :null}
                                                </div>
                                                {variable.inTemplate ?
                                                    <Alert severity="success">
                                                        <AlertTitle>Variable successfully added to template</AlertTitle>
                                                        <div style={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                                                            <div style={{paddingRight: 10, width: 118}}>Copy and paste ID into template:</div>
                                                            <TextField style={{width: 314}} InputProps={{ readOnly: true, classes: { input: "VarID" } }} defaultValue={"{" + variable.id + "}"} size="small"></TextField>
                                                            <IconButton onClick={() => highlightVarID(variable.id)} color="primary" aria-label="find variable" component="label">
                                                                <ManageSearchIcon style={{marginLeft: 2}} />
                                                            </IconButton>
                                                        </div>
                                                    </Alert> :
                                                    <Alert severity="warning">
                                                        <AlertTitle>Variable not found in template</AlertTitle>
                                                        <div style={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                                                            <div style={{paddingRight: 10, width: 118}}>Copy and paste ID into template:</div>
                                                            <TextField style={{width: 314}} InputProps={{ readOnly: true, classes: { input: "VarID" } }} defaultValue={"{" + variable.id + "}"} size="small"></TextField>
                                                        </div>
                                                    </Alert>
                                                }
                                            </Paper>
                                        )) :null}
                                    </Stack>
                                </SimpleBar>
                            </div>
                            <div style={{height: 10, width: 50}}></div>
                            <div style={{textAlign: "left"}}>
                                <h2>Template</h2>
                                <SimpleBar ref={simpleBarRef} style={{ marginTop: 30, width: 600, marginBottom: 60, maxHeight: "calc(100vh - 300px)" }}>
                                    <TextField ref={templateRef} multiline inputProps={{ spellCheck: 'false' }} value={entryTemplate || ''} onChange={(e) => setEntryTemplate(e.target.value)} id="TemplateBody" sx={{width: 600}}></TextField>
                                </SimpleBar>
                            </div>
                        </div>
                        <div style={{display: "flex", marginLeft: 24, marginTop: -60}}>
                                    <Button variant="success" onClick={saveTemplate}><FontAwesomeIcon icon={faCheck} /> Save Template</Button>
                                    <div style={{padding: 10}}></div>
                                    <Button variant="danger" onClick={() => window.location.reload()}><FontAwesomeIcon icon={faXmark} /> Discard Changes</Button>
                        </div>
                        <div style={{padding: 25}}></div>
                    </div> :<div><Alert severity="warning">You are not signed in. Please sign in as {editingUser?.email} to continue editing.</Alert></div>
                }
            </LocalizationProvider>
        </React.Fragment>
    );
}

export default BBCodeEditor;